/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef } from 'react';
import clsx from 'clsx';

import { LinksCollectionProps, LinksCollectionVariations, LinksCollectionAlignment } from './LinksCollection.types';
import { StyledLinkCollection } from './LinksCollection.styles';
import { Icon } from '../../atoms/Icon';
import { Link } from '../../atoms/Link';
import { Heading } from '../../atoms/Heading';
import { tags, headingType } from '../../utils/enums/enums';
import { useDocumentDirection } from '../../hooks';

export const LinksCollection: React.FC<LinksCollectionProps> = ({
  variation = LinksCollectionVariations.Standard,
  id,
  heading,
  hasIconArrow = true,
  linkListData,
  alignment = 'vertical-align',
  children,
  trackingProperties,
  ...props
}) => {
  const listCollectionClass =
    variation === LinksCollectionVariations.Standard
      ? `${clsx('cl__list', 'm-accordion__list', 'collapsed hide-visibility')}`
      : '';
  const linkListClass = variation === LinksCollectionVariations.Standard ? `${clsx('cl__list-item')}` : '';
  const linkItemClass = variation === LinksCollectionVariations.Standard ? `${clsx('cl__item-link t-font-s')}` : '';
  const customAttributes = trackingProperties?.enableScrollingBehavior
    ? { 'data-section-tracking': props?.componentId }
    : {};

  const dir = useDocumentDirection();
  const LinkRefs = useRef<HTMLAnchorElement[]>([]);

  return (
    <>
      {heading && (
        <Heading
          element={tags.h2}
          variation={headingType.subtitle}
          titleText={heading}
          customClass={props.headingClassName}
        />
      )}
      <StyledLinkCollection
        data-component-name="m-ui-library-LinksCollection"
        data-testid="ui-library-LinksCollection"
        id={id}
        className={clsx(
          listCollectionClass,
          props?.listCollectionClassname,
          alignment === LinksCollectionAlignment.Horizontal ? 'horizontal-align' : 'vertical-align'
        )}
        {...customAttributes}
      >
        {linkListData?.filter(Boolean)?.map((el: any, idx: number) => (
          <li className={clsx(linkListClass, props?.linkListClassName)} key={`${el}_${idx}`} id={el?.id}>
            <Link
              linkHref={el?.linkURL}
              linkClassName={clsx(
                linkItemClass,
                props?.listItemClassName,
                `${trackingProperties?.clickTrack ? 'custom_click_track' : ''}`,
                variation === LinksCollectionVariations.Standard && hasIconArrow && !el.displayExternalLinkIcon
                  ? el?.openInNewTab
                    ? dir === 'ltr'
                      ? 'icon-m icon-arrow-right-external'
                      : 'icon-m icon-arrow-left-external'
                    : ''
                  : '',
                el?.displayExternalLinkIcon ? 'd-inline-flex' : ''
              )}
              custom_click_track_value={
                trackingProperties?.clickTrack
                  ? `${trackingProperties?.location}|${el?.linkLabel}|${el?.openInNewTab ? 'external' : 'internal'}${
                      trackingProperties?.atCCeVar48 ? `|evar48=${trackingProperties?.atCCeVar48}` : ''
                    }`
                  : ''
              }
              trackingProperties={trackingProperties}
              rel={el?.openInNewTab ? 'noopener noreferrer' : ''}
              text={el?.linkLabel}
              target={el?.openInNewTab ? '_blank' : '_self'}
              role={props?.role}
              ref={element => {
                // @ts-expect-error no-explicit-any
                LinkRefs.current[idx] = element;
                return () => LinkRefs.current.splice(idx, 1);
              }}
            >
              {variation === LinksCollectionVariations.Standard && hasIconArrow && !el.displayExternalLinkIcon && (
                <Icon
                  iconClass={clsx(
                    'icon-m',
                    !el?.openInNewTab ? (dir === 'ltr' ? 'icon-arrow-right' : 'icon-arrow-left') : ''
                  )}
                />
              )}
              {variation === LinksCollectionVariations.Standard && el.displayExternalLinkIcon && (
                <Icon iconClass={clsx('icon icon-external-arrow ml-1', dir === 'rtl' && 'rtl-flip-arrow')} />
              )}
              <span className="sr-only">{el?.openInNewTab ? 'Opens a new window' : ''}</span>
              {children}
            </Link>
          </li>
        ))}
      </StyledLinkCollection>
    </>
  );
};
